import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, DatePicker, Modal, Popconfirm, Space, Table, Tag, TimePicker, message } from 'antd';
import dayjs from 'dayjs';
import { eventoService } from '../../services/api';
import { handleApiError } from '../../utils/apiError';
import TurmasElegiveisModal from './TurmasElegiveisModal';
import AlunosElegiveisModal from './AlunosElegiveisModal';
import SessaoInscricoesConfigModal from './SessaoInscricoesConfigModal';

// Fase A: só AGENDADA/CANCELADA -- "REALIZADA" não é persistido (ver
// backend/app/models/evento_sessao.py), então não há coluna/tag para
// isso aqui ainda.
const STATUS_COLORS = { AGENDADA: 'blue', CANCELADA: 'red' };

const HORA_FORMATO_API = 'HH:mm:ss';

const SessoesPainel = ({ evento, podeCriar, podeEditar, podeExcluir }) => {
  const [sessoes, setSessoes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalAberto, setModalAberto] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [form] = Form.useForm();

  const [sessaoTurmasAberta, setSessaoTurmasAberta] = useState(null);
  const [sessaoAlunosAberta, setSessaoAlunosAberta] = useState(null);
  const [sessaoInscricoesAberta, setSessaoInscricoesAberta] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await eventoService.listSessoes(evento.id, { page: 1, page_size: 200 });
      setSessoes(resp.data || []);
    } catch (err) {
      message.error('Erro ao carregar sessões');
    } finally {
      setLoading(false);
    }
  }, [evento.id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrirCriar = () => {
    setEditingId(null);
    form.resetFields();
    setModalAberto(true);
  };

  const abrirEditar = (sessao) => {
    setEditingId(sessao.id);
    form.setFieldsValue({
      nome: sessao.nome,
      data: dayjs(sessao.data),
      hora_inicio: dayjs(sessao.hora_inicio, HORA_FORMATO_API),
      hora_fim: sessao.hora_fim ? dayjs(sessao.hora_fim, HORA_FORMATO_API) : null,
      local: sessao.local,
    });
    setModalAberto(true);
  };

  const handleSubmit = async (values) => {
    const payload = {
      nome: values.nome,
      data: values.data.format('YYYY-MM-DD'),
      hora_inicio: values.hora_inicio.format(HORA_FORMATO_API),
      hora_fim: values.hora_fim ? values.hora_fim.format(HORA_FORMATO_API) : null,
      local: values.local || null,
    };
    setSalvando(true);
    try {
      if (editingId) {
        await eventoService.updateSessao(evento.id, editingId, payload);
        message.success('Sessão atualizada');
      } else {
        await eventoService.createSessao(evento.id, payload);
        message.success('Sessão criada');
      }
      setModalAberto(false);
      carregar();
    } catch (err) {
      handleApiError(err, 'Erro ao salvar sessão');
    } finally {
      setSalvando(false);
    }
  };

  const handleCancelar = async (sessao) => {
    try {
      await eventoService.cancelarSessao(evento.id, sessao.id);
      message.success('Sessão cancelada');
      carregar();
    } catch (err) {
      handleApiError(err, 'Erro ao cancelar sessão');
    }
  };

  const handleExcluir = async (sessao) => {
    try {
      await eventoService.deleteSessao(evento.id, sessao.id);
      message.success('Sessão excluída');
      carregar();
    } catch (err) {
      handleApiError(err, 'Não foi possível excluir a sessão');
    }
  };

  const columns = [
    { title: 'Nome', dataIndex: 'nome', key: 'nome' },
    { title: 'Data', dataIndex: 'data', key: 'data', render: (d) => dayjs(d).format('DD/MM/YYYY') },
    { title: 'Início', dataIndex: 'hora_inicio', key: 'hora_inicio', render: (h) => (h || '').slice(0, 5) },
    { title: 'Fim', dataIndex: 'hora_fim', key: 'hora_fim', render: (h) => (h ? h.slice(0, 5) : '—') },
    {
      // local NULL/vazio nunca cai automaticamente para o endereço da
      // Unidade -- Unidade é a organização dona do Evento, não o local
      // físico da sessão (decisão explícita da Fase A).
      title: 'Local',
      dataIndex: 'local',
      key: 'local',
      render: (local) => local || <span style={{ color: '#999' }}>Local a definir</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>,
    },
    {
      title: 'Inscrições',
      key: 'inscricoes',
      render: (_, sessao) => (
        <Space direction="vertical" size={2}>
          <Tag color={sessao.inscricoes_abertas ? 'green' : 'default'}>
            {sessao.inscricoes_abertas ? 'Abertas' : 'Fechadas'}
          </Tag>
          <span style={{ fontSize: 12, color: '#888' }}>
            Ocupação: {sessao.capacidade_total === null || sessao.capacidade_total === undefined
              ? `${sessao.ocupacao ?? 0} / Ilimitada`
              : `${sessao.ocupacao ?? 0}/${sessao.capacidade_total}`}
          </span>
        </Space>
      ),
    },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, sessao) => (
        <Space>
          {podeEditar && sessao.status === 'AGENDADA' && evento.status === 'ATIVO' && (
            <Button size="small" onClick={() => abrirEditar(sessao)}>
              Editar
            </Button>
          )}
          {podeEditar && (
            <Button size="small" onClick={() => setSessaoTurmasAberta(sessao)}>
              Turmas elegíveis
            </Button>
          )}
          {podeEditar && (
            <Button size="small" onClick={() => setSessaoAlunosAberta(sessao)}>
              Alunos elegíveis
            </Button>
          )}
          {podeEditar && (
            <Button size="small" onClick={() => setSessaoInscricoesAberta(sessao)}>
              Inscrições
            </Button>
          )}
          {podeEditar && sessao.status === 'AGENDADA' && (
            <Popconfirm
              title="Cancelar esta sessão?"
              onConfirm={() => handleCancelar(sessao)}
              okText="Confirmar"
              cancelText="Cancelar"
            >
              <Button size="small" danger>
                Cancelar
              </Button>
            </Popconfirm>
          )}
          {podeExcluir && (
            <Popconfirm
              title="Excluir definitivamente esta sessão?"
              description="Só é possível se a sessão não tiver histórico operacional."
              onConfirm={() => handleExcluir(sessao)}
              okText="Confirmar"
              cancelText="Cancelar"
            >
              <Button size="small" danger type="text">
                Excluir
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      <Space style={{ marginBottom: 12 }}>
        {podeCriar && evento.status === 'ATIVO' && (
          <Button size="small" type="primary" onClick={abrirCriar}>
            Nova Sessão
          </Button>
        )}
      </Space>
      <Table rowKey="id" size="small" loading={loading} dataSource={sessoes} columns={columns} pagination={false} />

      <Modal
        title={editingId ? 'Editar Sessão' : 'Nova Sessão'}
        open={modalAberto}
        onCancel={() => setModalAberto(false)}
        onOk={() => form.submit()}
        confirmLoading={salvando}
        okText="Salvar"
        cancelText="Cancelar"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="nome"
            label="Nome"
            rules={[{ required: true, whitespace: true, message: 'Informe o nome da sessão' }]}
          >
            <Input maxLength={150} placeholder='Ex.: "Ballet"' />
          </Form.Item>
          <Form.Item name="data" label="Data" rules={[{ required: true, message: 'Informe a data' }]}>
            <DatePicker format="DD/MM/YYYY" placeholder="Selecione a data" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="hora_inicio"
            label="Hora de início"
            rules={[{ required: true, message: 'Informe a hora de início' }]}
          >
            <TimePicker format="HH:mm" placeholder="Selecione o horário" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="hora_fim"
            label="Hora de término (opcional)"
            dependencies={['hora_inicio']}
            rules={[
              // Antecipa a mensagem de forma amigavel; a autoridade da regra
              // continua sendo o backend (schema Pydantic + CHECK
              // chk_tb_evento_sessoes_hora no banco) -- esta validacao so
              // melhora o feedback, nao substitui nenhuma das duas.
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const inicio = getFieldValue('hora_inicio');
                  if (!value || !inicio || value.isAfter(inicio)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Hora de término deve ser posterior à hora de início.'));
                },
              }),
            ]}
          >
            <TimePicker format="HH:mm" placeholder="Selecione o horário" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="local" label="Local">
            <Input placeholder='Ex.: "Teatro Ressurreição" (em branco = "Local a definir")' />
          </Form.Item>
        </Form>
      </Modal>

      <TurmasElegiveisModal
        open={sessaoTurmasAberta !== null}
        evento={evento}
        sessao={sessaoTurmasAberta}
        podeEditar={podeEditar}
        onClose={() => setSessaoTurmasAberta(null)}
      />

      <AlunosElegiveisModal
        open={sessaoAlunosAberta !== null}
        evento={evento}
        sessao={sessaoAlunosAberta}
        onClose={() => setSessaoAlunosAberta(null)}
      />

      <SessaoInscricoesConfigModal
        open={sessaoInscricoesAberta !== null}
        evento={evento}
        sessao={sessaoInscricoesAberta}
        podeEditar={podeEditar}
        onClose={() => setSessaoInscricoesAberta(null)}
        onAtualizado={carregar}
      />
    </div>
  );
};

export default SessoesPainel;
